import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import FacebookIcon from "@material-ui/icons/Facebook";
import InstagramIcon from "@material-ui/icons/Instagram";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

import { getTokens } from "../../styles/tokens";
import { StatusBadge, EmptyState } from "../../components/ui";
import { Card, SectionHead, MONO } from "./ui";
import { CopyButton } from "./CodeBlock";
import { maskToken } from "./snippets";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    tableWrap: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      minWidth: 820,
      "& tbody tr:last-child td": { borderBottom: "none" },
    },
    conn: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    avatar: {
      flex: "none",
      width: 32,
      height: 32,
      borderRadius: t.radius.sm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: t.tint.soft,
      color: t.brand,
      "& svg": { fontSize: 18 },
    },
    name: {
      fontFamily: t.font,
      fontSize: 14,
      fontWeight: 600,
      color: t.text,
      margin: 0,
      maxWidth: 240,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    meta: {
      fontFamily: t.font,
      fontSize: 12,
      color: t.textMuted,
      margin: 0,
      maxWidth: 240,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    tokenRow: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      maxWidth: 300,
      minWidth: 0,
    },
    token: {
      flex: 1,
      minWidth: 0,
      fontFamily: MONO,
      fontSize: 12.5,
      color: t.text,
      letterSpacing: "0.02em",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      userSelect: "all",
    },
    iconBtn: { padding: 6, color: t.textSecondary },
    noToken: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontFamily: t.font,
      fontSize: 12.5,
      color: t.textSecondary,
      "& svg": { fontSize: 16, color: t.textMuted, flex: "none" },
    },
    actions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      whiteSpace: "nowrap",
    },
    linkBtn: { "& .MuiButton-endIcon svg": { fontSize: 14 } },
    loading: { display: "flex", justifyContent: "center", padding: 32 },
  };
});

const channelIcon = (channel) => {
  if (channel === "facebook") return <FacebookIcon />;
  if (channel === "instagram") return <InstagramIcon />;
  return <WhatsAppIcon />;
};

const channelLabel = (channel) => {
  // TODO i18n
  if (channel === "whatsapp_oficial") return "WhatsApp Oficial (Meta)";
  if (channel === "facebook") return "Facebook";
  if (channel === "instagram") return "Instagram";
  return "WhatsApp";
};

const KeyRow = ({ wa, onUse }) => {
  const classes = useStyles();
  const [show, setShow] = useState(false);
  const hasToken = Boolean(wa.token);
  return (
    <TableRow>
      <TableCell>
        <div className={classes.conn}>
          <div className={classes.avatar}>{channelIcon(wa.channel)}</div>
          <div style={{ minWidth: 0 }}>
            <p className={classes.name} title={wa.name}>{wa.name}</p>
            <p className={classes.meta}>
              {channelLabel(wa.channel)}
              {wa.number ? ` · ${wa.number}` : ""}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={wa.status || "DISCONNECTED"} size="sm" />
      </TableCell>
      <TableCell>
        {hasToken ? (
          <div className={classes.tokenRow}>
            <span className={classes.token} title={show ? wa.token : undefined}>
              {show ? wa.token : maskToken(wa.token)}
            </span>
            {/* TODO i18n */}
            <Tooltip title={show ? "Ocultar" : "Revelar"}>
              <IconButton
                size="small"
                className={classes.iconBtn}
                aria-label={show ? "Ocultar token" : "Revelar token"}
                onClick={() => setShow((s) => !s)}
              >
                {show ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <CopyButton text={wa.token} plain label="Copiar token" />
          </div>
        ) : (
          <div className={classes.noToken}>
            <VpnKeyOutlinedIcon />
            {/* TODO i18n */}
            <span>Sem token. Abra a conexão em Conexões e salve para gerar.</span>
          </div>
        )}
      </TableCell>
      <TableCell align="right">
        <div className={classes.actions}>
          {hasToken && (
            <Button
              size="small"
              color="primary"
              endIcon={<ArrowForwardIcon style={{ fontSize: 14 }} />}
              onClick={() => onUse(wa)}
            >
              {/* TODO i18n */}
              Usar no construtor
            </Button>
          )}
          <Button
            size="small"
            component={RouterLink}
            to="/connections"
            className={classes.linkBtn}
            endIcon={<OpenInNewIcon />}
          >
            {/* TODO i18n */}
            {hasToken ? "Editar" : "Gerar token"}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

/**
 * Bloco 1 — chaves/conexões: tabela com nome, status, token mascarado (copiar/revelar).
 * Dados vêm de GET /whatsapp (campo `token`); o token é gerado ao salvar a conexão em /connections.
 */
const KeysSection = ({ whatsApps = [], loading, onUse }) => {
  const classes = useStyles();
  const withToken = whatsApps.filter((w) => w.token).length;

  return (
    <Card id="api-keys">
      <SectionHead
        step={1}
        // TODO i18n
        title="Suas chaves de API"
        description="Cada conexão tem um token próprio. Ele vai no cabeçalho Authorization: Bearer de toda requisição."
        right={
          <>
            {whatsApps.length > 0 && (
              <StatusBadge
                color={withToken ? "success" : "warning"}
                size="sm"
                label={`${withToken}/${whatsApps.length} com token`}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to="/connections"
            >
              {/* TODO i18n */}
              Gerenciar conexões
            </Button>
          </>
        }
      />

      {loading ? (
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      ) : whatsApps.length === 0 ? (
        <EmptyState
          icon={<VpnKeyOutlinedIcon />}
          // TODO i18n
          title="Nenhuma conexão cadastrada"
          message="Crie uma conexão em Conexões. O token da API é gerado automaticamente ao salvar."
          action={
            <Button variant="contained" color="primary" component={RouterLink} to="/connections">
              Ir para Conexões
            </Button>
          }
        />
      ) : (
        <div className={classes.tableWrap}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                {/* TODO i18n */}
                <TableCell>Conexão</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Token</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {whatsApps.map((wa) => (
                <KeyRow key={wa.id} wa={wa} onUse={onUse} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
};

export default KeysSection;
